import { Chip } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import { memo } from 'react'
import { VolumeType } from './utils/volumeTypeMapping'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Tooltip from 'core/elements/tooltip/Tooltip'

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  label: {
    color: theme.palette.grey[700],
    whiteSpace: 'nowrap',
    lineHeight: '24px', // Align with chip height
  },
  chip: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 0.5),
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.grey[800],
    height: 24,
    borderRadius: 0,
  },
  loadingText: {
    color: theme.palette.grey[500],
    fontStyle: 'italic',
  },
  noTypesText: {
    color: theme.palette.grey[500],
    fontStyle: 'italic',
  },
}))

export interface AssignedVolumeTypesProps {
  assignedTypes: VolumeType[]
  loading?: boolean
  label?: string
  maxVisible?: number
  showEmpty?: boolean
  emptyMessage?: string
  removeVolumeType: (volumeType: VolumeType) => void
}

/**
 * Displays assigned volume types as chips.
 * Memoized for performance - only re-renders when props change.
 */
function AssignedVolumeTypesComponent({
  assignedTypes,
  loading = false,
  label = 'Assigned Volume Types:',
  maxVisible,
  showEmpty = true,
  emptyMessage = 'No volume types assigned to this backend',
  removeVolumeType,
}: AssignedVolumeTypesProps) {
  const classes = useStyles()

  if (loading) {
    return (
      <div className={classes.container}>
        <Text variant="body2" className={classes.label}>
          {label}
        </Text>
        <Text variant="body2" className={classes.loadingText}>
          Loading...
        </Text>
      </div>
    )
  }

  const hasTypes = assignedTypes.length > 0
  const visibleTypes = maxVisible ? assignedTypes.slice(0, maxVisible) : assignedTypes
  const hiddenCount = maxVisible ? Math.max(0, assignedTypes.length - maxVisible) : 0

  if (!hasTypes && !showEmpty) {
    return null
  }

  return (
    <div className={classes.container}>
      {hasTypes ? (
        <>
          <Text variant="body2" className={classes.label}>
            {label}
          </Text>
          {visibleTypes.map((volumeType) => (
            <Chip
              key={volumeType.id}
              label={volumeType.name}
              size="small"
              className={classes.chip}
              deleteIcon={
                !volumeType?.id ? (
                  <Tooltip message={'Remove Volume Type'}>
                    <FontAwesomeIcon solid size="lg">
                      times
                    </FontAwesomeIcon>
                  </Tooltip>
                ) : null
              }
              onDelete={!volumeType?.id ? () => removeVolumeType(volumeType) : undefined}
            />
          ))}
          {hiddenCount > 0 && (
            <Chip label={`+${hiddenCount} more`} size="small" className={classes.chip} />
          )}
        </>
      ) : (
        <Text variant="body2" className={classes.noTypesText}>
          {emptyMessage}
        </Text>
      )}
    </div>
  )
}

export const AssignedVolumeTypes = memo(AssignedVolumeTypesComponent)
export default AssignedVolumeTypes
